'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  buildGTMPromotionEcommerce,
  GTMPromotionSource,
  sendGTMEventPromotion
} from '../lib/promotion';
import { getPageCategory } from '../utils/getPageCategory';

export type UseGTMPromotionBannerParams = {
  creativeName?: string;
  promotionId?: string;
  promotionName?: string;
  source: GTMPromotionSource;
  index?: number;
  enabled?: boolean;
};

export function useGTMPromotionBanner({
  creativeName,
  promotionId,
  promotionName,
  source,
  index = 1,
  enabled = true
}: UseGTMPromotionBannerParams) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      setElement(enabled ? node : null);
    },
    [enabled]
  );
  const pathname = usePathname();
  const sendMultiple = source === 'slider';

  const ecommerce = useMemo(
    () =>
      buildGTMPromotionEcommerce({
        creativeName,
        promotionId,
        promotionName,
        pageCategory: getPageCategory(pathname),
        source,
        index
      }),
    [creativeName, promotionId, promotionName, pathname, source, index]
  );

  useEffect(() => {
    if (!enabled || !element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            sendGTMEventPromotion('view_promotion', ecommerce);
            if (!sendMultiple) {
              observer.disconnect();
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [enabled, ecommerce, sendMultiple, element]);

  const trackSelectPromotion = useCallback(() => {
    if (!enabled) return;
    sendGTMEventPromotion('select_promotion', ecommerce);
  }, [ecommerce, enabled]);

  return { ref, trackSelectPromotion };
}
